/** Where a code stands in the config's own order; undeclared codes after every declared one. */
export var configRank = function (fieldDef) {
    var declared = new Map();
    fieldDef.subfields.forEach(function (sf, i) { if (!declared.has(sf.code))
        declared.set(sf.code, i); });
    return function (code) { var _a; return (_a = declared.get(code)) !== null && _a !== void 0 ? _a : Number.MAX_SAFE_INTEGER; };
};
var codeOf = function (row) { var _a; return (_a = row.subfield_code) !== null && _a !== void 0 ? _a : ''; };
/**
 * `rows` in the stored order `order` names — one code per occurrence.
 *
 * <p>Which row of a repeated code takes which occurrence follows from how the
 * load split them: the first occurrence went into the form control, the rest
 * were carried (CZNA-722). So the `carried` rows take the LAST occurrences of
 * their code and the others the first ones. Matched front to front instead,
 * a `110 $a $n $t $n` whose first `$n` the cataloguer emptied saved the carried
 * part number before `$t` (blind review r3). A multi-value control holds every
 * occurrence itself and has no such split; no name-title field the templates
 * declare has one (AUTHOR_WORK 100 and 400, checked 2026-10-19).
 *
 * <p>A row `order` does not account for (a subfield the cataloguer filled in
 * after loading, another value of a repeatable code) is placed after the last
 * row of its own code, or else after the last row whose code comes before it
 * in the config; failing both, first. A code `order` names that no longer has a
 * row (the cataloguer emptied it) is skipped.
 *
 * <p>`phantom` rows stand for an empty row the form draws (CZNA-1422): they take
 * their place in `order` like any other row, but a real row never anchors on
 * one, so the real rows come out in exactly the order they would without them
 * — the order the save stores.
 */
export var restoreSubfieldOrder = function (rows, order, rank, carried, phantom) {
    if (carried === void 0) { carried = new Set(); }
    if (phantom === void 0) { phantom = new Set(); }
    var used = new Set();
    var slots = order.map(function () { return undefined; });
    var _loop_1 = function (i) {
        var candidates = rows.filter(function (r) { return carried.has(r) && !used.has(r) && codeOf(r) === order[i]; });
        var last = candidates[candidates.length - 1];
        if (last) {
            used.add(last);
            slots[i] = last;
        }
    };
    for (var i = order.length - 1; i >= 0; i -= 1) {
        _loop_1(i);
    }
    order.forEach(function (code, i) {
        if (slots[i])
            return;
        var next = rows.find(function (r) { return !used.has(r) && !carried.has(r) && codeOf(r) === code; });
        if (next) {
            used.add(next);
            slots[i] = next;
        }
    });
    var placed = slots.filter(function (r) { return r !== undefined; });
    rows.filter(function (r) { return !used.has(r); }).forEach(function (row) {
        var code = codeOf(row);
        var sameCode = -1;
        var before = -1;
        placed.forEach(function (p, i) {
            if (phantom.has(p) && !phantom.has(row))
                return;
            var pc = codeOf(p);
            if (pc === code)
                sameCode = i;
            else if (rank(pc) <= rank(code))
                before = i;
        });
        placed.splice((sameCode >= 0 ? sameCode : before) + 1, 0, row);
    });
    return placed;
};
/**
 * The order to remember for a loaded field instance, or `undefined` when the
 * save would emit that order anyway.
 *
 * <p>What the save emits without `_order`: the codes the instance holds, in
 * config order, then the passthrough rows grouped by code in the order each
 * code first appeared. `carried` is the set of rows that went to passthrough,
 * so a code with one row in a control and another carried (CZNA-722) is placed
 * the way the save will place it.
 */
export var loadedSubfieldOrder = function (fieldDef, rows, carried) {
    // A code that is not one character cannot be written into `_order` and read back.
    if (rows.some(function (r) { return codeOf(r).length !== 1; }))
        return undefined;
    var rank = configRank(fieldDef);
    var firstSeen = new Map();
    rows.forEach(function (r, i) { if (!firstSeen.has(codeOf(r)))
        firstSeen.set(codeOf(r), i); });
    var keyOf = function (r) { return (carried.has(r) ? Number.MAX_SAFE_INTEGER / 2 + firstSeen.get(codeOf(r)) : rank(codeOf(r))); };
    var arrived = rows.map(codeOf);
    var emitted = rows
        .map(function (r, i) { return ({ r: r, i: i }); })
        .sort(function (a, b) { return (keyOf(a.r) - keyOf(b.r)) || (a.i - b.i); })
        .map(function (_a) {
        var r = _a.r;
        return codeOf(r);
    });
    return arrived.join('\u0000') === emitted.join('\u0000') ? undefined : arrived;
};
/** `_order` as stored in the instance — a code per character, MARC codes being one character. */
export var instanceOrder = function (instance) {
    var _a;
    var value = (_a = instance === null || instance === void 0 ? void 0 : instance._order) === null || _a === void 0 ? void 0 : _a.value;
    return value ? value.split('') : undefined;
};
